import { useState } from 'react';
import { ArrowRight, Check } from '@phosphor-icons/react';
import { useTheme } from '@/contexts/ThemeContext';
import { useSEO } from '@/hooks/useSEO';
import PublicNav from '@/components/layout/PublicNav';
import PublicFooter from '@/components/layout/PublicFooter';

const BRANCH_OPTIONS = ['1', '2-5', '6-10', '10+'];

export default function ContactPage() {
  const { theme } = useTheme();
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '',
    institute: '',
    phone: '',
    email: '',
    branches: '',
    message: '',
  });

  useSEO({
    title: 'Contact Us — Admitrix Admissions CRM',
    description: 'Request a demo of Admitrix. Get in touch with our team to learn how Admitrix can transform your admissions process.',
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      await fetch(`${backendUrl}/api/webhooks/generic`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source: 'contact_form', data: form }),
      });
    } catch {
      // Fallback: store in localStorage if backend unavailable
      const existing = JSON.parse(localStorage.getItem('contact_submissions') || '[]');
      existing.push({ ...form, timestamp: new Date().toISOString() });
      localStorage.setItem('contact_submissions', JSON.stringify(existing));
    }
    setSubmitted(true);
  };

  return (
    <div className="bg-background text-foreground overflow-x-hidden">
      <PublicNav />

      <main>
        <section className="pt-32 pb-24 px-6 lg:px-12 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Left: Info */}
            <div>
              <p className="text-[10px] font-mono uppercase tracking-[0.15em] mb-4" style={{ color: '#E04B2A' }}>CONTACT US</p>
              <h1 className="font-display font-extrabold text-[clamp(2.2rem,4.5vw,3.5rem)] tracking-[-0.03em] leading-[1.05] text-foreground">
                Let's talk about<br />your institute.
              </h1>
              <p className="text-lg text-muted-foreground mt-6 leading-relaxed max-w-md">
                Tell us about your needs and we'll set up a personalized demo. No commitment, no pressure.
              </p>

              <div className="mt-10 space-y-4">
                {[
                  'Personalized walkthrough of the platform',
                  'See how it fits your specific workflow',
                  'Get answers from our product team',
                  'No credit card or commitment required',
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#E04B2A15', color: '#E04B2A' }}>
                      <Check size={12} weight="bold" />
                    </div>
                    <span className="text-[13px] text-foreground">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Form */}
            <div className="rounded-xl border border-border bg-card p-8" data-testid="contact-form-card">
              {submitted ? (
                <div className="text-center py-12" data-testid="contact-success">
                  <div className="w-14 h-14 rounded-xl mx-auto mb-6 flex items-center justify-center" style={{ backgroundColor: '#E04B2A15', color: '#E04B2A' }}>
                    <Check size={28} weight="bold" />
                  </div>
                  <h2 className="font-display text-2xl font-extrabold text-foreground mb-2">We'll be in touch!</h2>
                  <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                    Thanks, {form.name}. Our team will reach out within 24 hours to schedule your demo.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <h2 className="font-display text-lg font-bold text-foreground mb-1">Request a Demo</h2>
                  <p className="text-[12px] text-muted-foreground mb-4">Fill in your details and we'll get back to you shortly.</p>

                  <div>
                    <label className="block text-[12px] font-semibold text-foreground mb-1.5">Name *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={form.name}
                      onChange={handleChange}
                      className="w-full h-10 px-3 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-border"
                      placeholder="Your full name"
                      data-testid="contact-name"
                    />
                  </div>

                  <div>
                    <label className="block text-[12px] font-semibold text-foreground mb-1.5">Institute Name *</label>
                    <input
                      type="text"
                      name="institute"
                      required
                      value={form.institute}
                      onChange={handleChange}
                      className="w-full h-10 px-3 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-border"
                      placeholder="e.g. Apex Academy"
                      data-testid="contact-institute"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[12px] font-semibold text-foreground mb-1.5">Phone *</label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={form.phone}
                        onChange={handleChange}
                        className="w-full h-10 px-3 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-border"
                        placeholder="+91 98765 43210"
                        data-testid="contact-phone"
                      />
                    </div>
                    <div>
                      <label className="block text-[12px] font-semibold text-foreground mb-1.5">Email *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={form.email}
                        onChange={handleChange}
                        className="w-full h-10 px-3 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-border"
                        placeholder="you@institute.com"
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[12px] font-semibold text-foreground mb-1.5">Number of Branches</label>
                    <select
                      name="branches"
                      value={form.branches}
                      onChange={handleChange}
                      className="w-full h-10 px-3 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-border"
                      data-testid="contact-branches"
                    >
                      <option value="">Select...</option>
                      {BRANCH_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[12px] font-semibold text-foreground mb-1.5">Message</label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      rows={3}
                      className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-border resize-none"
                      placeholder="Tell us about your requirements (optional)"
                      data-testid="contact-message"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full h-11 rounded-lg text-sm font-bold text-white flex items-center justify-center gap-2 transition-transform hover:scale-[1.02] active:scale-[0.98]"
                    style={{ backgroundColor: '#E04B2A' }}
                    data-testid="contact-submit"
                  >
                    Request a Demo <ArrowRight size={16} weight="bold" />
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
